import type { VisibilityScope } from "@/components/agentic-v2/VisibilityFilter";
import type { ClientsOnboardingScope } from "@/services/api/agentic-v2/types";
import type { HomeSurfaceUserContext } from "@/components/home/resolveHomeSurfaceId";

/** Pulse journey group ids accepted by GET /fa-clients?stage_group= */
export type ClientStageGroup = "in_dg" | "in_paraplanning" | "in_advice";

/** One stage filter pill on the Clients list (persona-configured). */
export interface ClientStageFilter {
  id: string;
  label: string;
  /** When set, passed as ``stage_group`` to the list API. */
  stageGroup?: ClientStageGroup;
}

/** Top-level view within the Clients app workbench. */
export type ClientsListLens = "focus" | "all" | "action" | "healthy";

export interface ClientsAppProfile {
  id: string;
  defaultLens: ClientsListLens;
  lenses: ClientsListLens[];
  defaultVisibility: VisibilityScope;
  showInsightBuckets: boolean;
  /** Persona-specific journey stage pills (Pulse v2 aligned). */
  stageFilters?: ClientStageFilter[];
  defaultStageFilter?: string;
  /** Toggle active onboarding mission vs full stage caseload (paraplanner list). */
  showOnboardingScopeToggle?: boolean;
  defaultOnboardingScope?: ClientsOnboardingScope;
}

const DEFAULT_PROFILE: ClientsAppProfile = {
  id: "default",
  defaultLens: "all",
  lenses: ["action", "healthy", "all"],
  defaultVisibility: "all",
  showInsightBuckets: true,
};

const OCTOPUS_PARAPLANNER_STAGE_FILTERS: ClientStageFilter[] = [
  { id: "in_dg", label: "In DG", stageGroup: "in_dg" },
  { id: "in_paraplanning", label: "In paraplanning", stageGroup: "in_paraplanning" },
  { id: "in_advice", label: "In advice", stageGroup: "in_advice" },
  { id: "all", label: "All stages" },
];

/** Paraplanner Clients workbench — explicit role only (home module is not used for Octopus paraplanners). */
export function isParaplannerPersona(user?: HomeSurfaceUserContext | null): boolean {
  return (user?.role ?? "").toLowerCase() === "paraplanner";
}

const OCTOPUS_PARAPLANNER_PROFILE: ClientsAppProfile = {
  id: "octopus_paraplanner",
  defaultLens: "all",
  lenses: ["all"],
  defaultVisibility: "me",
  showInsightBuckets: false,
  stageFilters: OCTOPUS_PARAPLANNER_STAGE_FILTERS,
  defaultStageFilter: "in_dg",
  showOnboardingScopeToggle: true,
  defaultOnboardingScope: "onboarding",
};

/** Resolve Clients app layout from company + paraplanner persona (stage pills, onboarding scope). */
export function resolveClientsAppProfile(
  companyId: string | null | undefined,
  user?: HomeSurfaceUserContext | null,
): ClientsAppProfile {
  const normalized = (companyId || "").toLowerCase();
  if (normalized === "octopus" && isParaplannerPersona(user)) {
    return OCTOPUS_PARAPLANNER_PROFILE;
  }
  return DEFAULT_PROFILE;
}

export function lensLabel(lens: ClientsListLens): string {
  switch (lens) {
    case "focus":
      return "Focus";
    case "all":
      return "All clients";
    case "action":
      return "Action needed";
    case "healthy":
      return "Healthy";
    default:
      return lens;
  }
}

/** Resolve the active stage filter pill from profile + selected id. */
export function resolveStageFilter(
  profile: ClientsAppProfile,
  selectedId: string,
): ClientStageFilter | undefined {
  return profile.stageFilters?.find((pill) => pill.id === selectedId);
}
